import { IRequestContext } from '@msdyn365-commerce/core-internal';
import { clearCookie, getCookieValueFromRequest, saveCookie } from '../../shared/functions/cookie';
import { LEGACY_CONTROLLER_ID_COOKIE_NAME, UCConsentServiceNames, UCServiceControllerIdCookieNames } from './const';
import { UCServiceInfo, UCUI } from './types';

export class UserCentricsService {
    private request: IRequestContext | undefined;

    constructor(request?: IRequestContext) {
        this.request = request;
    }

    public hasLoaded(): boolean {
        return !!this.getUCUI();
    }

    public async hasServiceConsent(serviceName: UCConsentServiceNames): Promise<boolean | undefined> {
        const services = await this.getServicesBaseInfo();
        return services?.some(service => service.name === serviceName && !!service.consent.status);
    }

    public async hasFunctionalConsent(): Promise<boolean | undefined> {
        const services = await this.getServicesBaseInfo();
        return services?.some(service => service.categorySlug === 'functional' && service.consent.status);
    }

    public async getControllerId(): Promise<string | undefined> {
        const ucUI = this.getUCUI();
        if (!ucUI) {
            return;
        }
        return ucUI.getControllerId();
    }

    public async getConsentedControllerId(serviceCookie: UCServiceControllerIdCookieNames): Promise<string | undefined> {
        if (typeof window === 'undefined') {
            return this.getControllerIdFromRequestCookies(serviceCookie);
        }
        return this.getControllerId();
    }

    public async setServiceControllerIdCookie(
        serviceName: UCConsentServiceNames,
        cookieName: UCServiceControllerIdCookieNames,
        controllerId: string
    ): Promise<void> {
        if (!this.request) {
            return;
        }

        const hasConsent = await this.hasServiceConsent(serviceName);
        if (!hasConsent) {
            clearCookie(cookieName);
            return;
        }

        saveCookie(cookieName, controllerId);
        // TODO: Reenable this when we figure out why the request.cookies methods aren't working
        // setCookieFromRequest(cookieName, controllerId, this.request);
    }

    public removeServiceControllerIdCookie(cookieName: UCServiceControllerIdCookieNames): void {
        if (!this.request) {
            return;
        }

        clearCookie(cookieName);
        // TODO: Reenable this when we figure out why the request.cookies methods aren't working
        // removeCookieFromRequest(cookieName, this.request);
    }

    private getUCUI(): UCUI | undefined {
        if (typeof window === 'undefined') {
            return;
        }
        return window.UC_UI;
    }

    private async getServicesBaseInfo(): Promise<UCServiceInfo[] | undefined> {
        return this.getUCUI()?.getServicesBaseInfo();
    }

    private getControllerIdFromRequestCookies(serviceCookie: UCServiceControllerIdCookieNames): string | undefined {
        if (!this.request) {
            return;
        }
        return (
            getCookieValueFromRequest(serviceCookie, this.request) ??
            // TODO: Legacy cookie needs to be removed
            getCookieValueFromRequest(LEGACY_CONTROLLER_ID_COOKIE_NAME, this.request)
        );
    }
}
